import type { TbiConfigType } from '../types'

export interface TbiInstallmentResult {
  visible: boolean
  installment: number
}

/**
 * Compute the TBI Bank FusionPay monthly installment for a given price.
 *
 * Mirrors the authoritative server-side initial render
 * TbiBank\FusionPay\Helper\Data::getOptions(): the BNPL rate applies below `bnplMax`,
 * otherwise the matching standard-credit tier is used. A no-tier-match keeps the BNPL
 * values (same as the phtml).
 *
 * Returns `visible: false` when the card should be hidden: feature disabled, price
 * outside the [minimumVisible, maximumVisible] range, or a multiplier of 1 (no
 * financing benefit — the native button hides in that case too).
 */
export function computeTbiInstallment(
  price: number,
  config: TbiConfigType
): TbiInstallmentResult {
  const hidden: TbiInstallmentResult = { visible: false, installment: 0 }

  if (!config.enabled) return hidden
  if (price < config.minimumVisible || price > config.maximumVisible) return hidden

  let multiplier = config.bnplMultiplier
  let analysisFee = config.bnplAnalysisFee

  if (price > config.bnplMax) {
    let prevLimit = config.bnplMax
    for (const tier of config.tiers) {
      if (price > prevLimit && price <= tier.limit) {
        multiplier = tier.multiplier
        analysisFee = config.standardAnalysisFee
        break
      }
      prevLimit = tier.limit
    }
  }

  if (multiplier === 1) return hidden

  return { visible: true, installment: multiplier * (price + analysisFee) }
}

export interface TbiLabel {
  /** BNPL/standard threshold, formatted for display (e.g. "2000 lei"). */
  thresholdLabel: string
  /** BNPL installment count (0 when not configured). */
  bnplPeriod: number
  /** Standard-credit installment range (e.g. "60" or "6-60"). */
  standardPeriodLabel: string
  /** Whether a standard tier is configured — drives the second badge. */
  hasStandardTier: boolean
}

/**
 * Derive the two-tier TBI label from config: BNPL (0% below `bnplMax`) vs standard
 * fixed credit (above it). Every number comes straight from tbiConfig — no
 * hardcoded amounts or periods. Keeps the derivation out of the component.
 */
export function buildTbiLabel(config: TbiConfigType): TbiLabel {
  const tierPeriods = config.tiers.map((tier) => tier.period).filter((period) => period > 0)
  const minTierPeriod = tierPeriods.length ? Math.min(...tierPeriods) : 0
  const maxTierPeriod = tierPeriods.length ? Math.max(...tierPeriods) : 0

  return {
    thresholdLabel: `${Math.round(config.bnplMax)} lei`,
    bnplPeriod: config.bnplPeriod,
    standardPeriodLabel:
      minTierPeriod === maxTierPeriod ? `${maxTierPeriod}` : `${minTierPeriod}-${maxTierPeriod}`,
    hasStandardTier: maxTierPeriod > 0,
  }
}
